import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { Label, NumberInput, Switch, HelpButton } from '@/components/ui';
import { AnimatedFormError } from '@/components/form/AnimatedFormError';
import type { MealBreakConfig, Role } from '@/types';
import { fieldDescriptions } from '../utils/loadDescriptions';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { ClickableDescription } from '../components/ClickableDescription';
import { ApplicableRolesRow } from '../../shared/AdvancedSettingsSection';
import { CountModeSettingsSection } from './CountModeSettingsSection';
import { fieldLabelClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { buildMealTriggerModeInjectable } from '../utils/mealTriggerModeInjectable';
import { allApplicableRolesAreDayRate } from '../../shared/utils';
import { useMethodSwapAnimation } from '../../../config_forms/shared/surcharge/useMethodSwapAnimation';
import { SurchargeMethodPanel } from '../../../config_forms/shared/surcharge/SurchargeMethodPanel';
import { SegmentedControl } from '@/components/ui/segmented-control';

const TRIGGER_MODE_OPTIONS = [
  { value: 'deadline', label: 'Deadline' },
  { value: 'count', label: 'Break Count' },
];

/** Map surcharge types that are exclusive to one trigger mode to their counterpart,
 *  and clear fields forbidden by DB CHECK constraints in the target mode.
 *  Fires synchronously in the same onMealConfigChange call as the trigger-mode flip,
 *  so the parent's compatibility useEffect sees a consistent post-swap payload. */
function getSurchargeTypeSwap(
  targetMode: 'deadline' | 'count',
  currentType: MealBreakConfig['mealSurchargeType'],
): Partial<MealBreakConfig> {
  if (targetMode === 'count' && currentType === 'guaranteed_rate') {
    // guaranteed_rate's floor semantics don't compose with miss count — swap to fixed_fee.
    return {
      mealSurchargeType: 'fixed_fee',
      floorSource: 'none',
      overtimeOffset: 0,
      floorTierCode: null,
      floorTierCustomValue: null,
    };
  }
  if (targetMode === 'count' && currentType === 'offset_overtime') {
    // Preserve the method + extended-overtime config; clear only the time-gated
    // escalation fields forbidden by count_mode_offset_no_escalation_check.
    return {
      useAdditionalOffset: false,
      stepUpAfterMinutes: null,
    };
  }
  return {};
}

interface Props {
  context: 'user' | 'client';
  mealConfigCustomized: Partial<Record<keyof MealBreakConfig, boolean>>;
  safeConfig: MealBreakConfig;
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  mealDeadlineHoursProps?: Record<string, unknown>;
  mealDeadlineHoursError?: React.ReactNode;
  gracePeriodMinutesProps?: Record<string, unknown>;
  gracePeriodMinutesError?: React.ReactNode;
  anyHalfDayRoles: boolean;
  roles?: Role[];
  halfDayHours?: number | null;
  minimumBillableHours?: number | null;
  halfDayBreaksProps?: Record<string, unknown>;
  halfDayBreaksError?: React.ReactNode;
}

export const BaseSettingsSection: React.FC<Props> = ({
  context,
  mealConfigCustomized,
  safeConfig,
  onMealConfigChange,
  mealDeadlineHoursProps,
  mealDeadlineHoursError,
  gracePeriodMinutesProps,
  gracePeriodMinutesError,
  anyHalfDayRoles,
  roles = [],
  halfDayHours,
  minimumBillableHours,
  halfDayBreaksProps,
  halfDayBreaksError,
}) => {
  const triggerMode = safeConfig.mealTriggerMode || 'deadline';
  const isDeadlineMode = triggerMode === 'deadline';
  const isCountMode = triggerMode === 'count';

  const {
    containerStyle,
    containerRef,
    activePanelRef,
    fadeDuration,
  } = useMethodSwapAnimation(triggerMode);

  const allDayRate = allApplicableRolesAreDayRate(roles, safeConfig.applicableRoleIds);

  const triggerModeInjectable = React.useMemo(() => {
    return buildMealTriggerModeInjectable({
      mealBreakConfig: safeConfig,
      onModeChange: (mode) => onMealConfigChange({
        mealTriggerMode: mode,
        ...getSurchargeTypeSwap(mode, safeConfig.mealSurchargeType),
        ...(mode === 'count' ? { applyOnHalfDays: true } : {}),
      }),
      // Deadline callbacks
      onDeadlineChange: (value) => onMealConfigChange({ mealDeadlineHours: value }),
      onMultipleBreaksChange: (value) => onMealConfigChange({ recurringMealDeadline: value }),
      onGracePeriodChange: (value) => onMealConfigChange({ gracePeriodMinutes: value }),
      // Count callbacks
      onRequiredBreaksChange: (value) => onMealConfigChange({ requiredBreaks: value }),
      onScalingToggle: (enabled) => {
        if (enabled) {
          const updates: Partial<MealBreakConfig> = { additionalBreakIntervalHours: 4 };
          if (safeConfig.thresholdHours == null) {
            updates.thresholdHours = 10;
          }
          onMealConfigChange(updates);
        } else {
          onMealConfigChange({ additionalBreakIntervalHours: null });
        }
      },
      onThresholdChange: (value) => onMealConfigChange({ thresholdHours: value }),
      onAdditionalBreakIntervalChange: (value) => onMealConfigChange({ additionalBreakIntervalHours: value }),
      thresholdFromBilling: allDayRate && minimumBillableHours != null ? minimumBillableHours : null,
    });
  }, [safeConfig, onMealConfigChange, allDayRate, minimumBillableHours]);

  // Controls the `apply_on_half_days` toggle visibility.
  // - Deadline mode: hide when deadline >= half-day length (surcharge can never trigger).
  // - Count mode: hidden. The Half-Day Breaks toggle in CountModeSettingsSection is
  //   the sole half-day gate (required_breaks_half_day IS NULL → 0 breaks → no
  //   surcharge). applyOnHalfDays is forced to true in count mode so the view-level
  //   filter doesn't also gate the surcharge.
  const showHalfDayToggle = isDeadlineMode
    ? (safeConfig.mealDeadlineHours ?? 0) < (halfDayHours ?? Infinity)
    : false;

  const rolesRow = (
    <ApplicableRolesRow
      roles={roles}
      applicableRoleIds={safeConfig.applicableRoleIds ?? null}
      onApplicableRoleIdsChange={(roleIds) => onMealConfigChange({ applicableRoleIds: roleIds })}
      isCustomized={mealConfigCustomized.applicableRoleIds}
      context={context}
      featureName="meal-surcharge"
      showHalfDayOption={anyHalfDayRoles && showHalfDayToggle}
      applyOnHalfDays={safeConfig.applyOnHalfDays}
      onApplyOnHalfDaysChange={(checked) => onMealConfigChange({ applyOnHalfDays: checked })}
    />
  );

  return (
    <div>
      {/* Trigger Mode Selector */}
      <div>
        <div className="grid grid-cols-1 gap-y-1 md:grid-cols-2 md:gap-4 md:items-center">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <Label className={fieldLabelClass}>Surcharge Trigger</Label>
                <CustomizedFieldIndicator isCustomized={mealConfigCustomized.mealTriggerMode} context={context} />
              </div>
              <HelpButton
                fieldName="Surcharge Trigger"
                content={fieldDescriptions.meal_trigger_mode}
                enableFieldFocus
                injectableComponents={triggerModeInjectable.injectables}
              />
            </div>
            <div className="hidden md:block">
              <ClickableDescription>
                Require a meal break every X hours, or require X total meal breaks
              </ClickableDescription>
            </div>
          </div>
          <div className="flex justify-start md:justify-end md:col-start-2" data-anim-audit="meal-trigger-mode-segmented">
            <SegmentedControl
              ariaLabel="Trigger Mode"
              options={TRIGGER_MODE_OPTIONS}
              value={triggerMode}
              onChange={(v) => {
                const mode = v as 'deadline' | 'count';
                onMealConfigChange({
                  mealTriggerMode: mode,
                  ...getSurchargeTypeSwap(mode, safeConfig.mealSurchargeType),
                  ...(mode === 'count' ? { applyOnHalfDays: true } : {}),
                });
              }}
              className="w-full md:w-auto md:min-w-[260px]"
            />
          </div>
          <div className="md:hidden">
            <ClickableDescription>
              Require a meal break every X hours, or require X total meal breaks
            </ClickableDescription>
          </div>
        </div>
      </div>

      {/* Mode Fields — crossfade + animated height via useMethodSwapAnimation */}
      <div
        ref={containerRef}
        className="mt-4 overflow-hidden"
        style={containerStyle}
        data-anim-audit="trigger-mode-panel-container"
      >
        <div className="relative grid">
          <AnimatePresence mode="sync" initial={false}>
            {isDeadlineMode && (
              <SurchargeMethodPanel key="deadline" activePanelRef={activePanelRef} transitionDuration={fadeDuration}>
                <div className="space-y-4">
                  <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="meal-window-threshold" className={fieldLabelClass}>Meal Break Deadline</Label>
                        <CustomizedFieldIndicator isCustomized={mealConfigCustomized.mealDeadlineHours} context={context} />
                      </div>
                      <div className="hidden md:block">
                        <ClickableDescription>
                          The maximum hours of continuous work allowed before a meal break must be provided.
                        </ClickableDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end md:items-end md:w-full">
                      <NumberInput
                        id="meal-window-threshold"
                        unit="hrs"
                        size="xs"
                        minValue={0}
                        maxValue={99}
                        {...(mealDeadlineHoursProps || {})}
                      />
                      <AnimatedFormError className="w-full text-left md:text-right mt-1">
                        {mealDeadlineHoursError}
                      </AnimatedFormError>
                    </div>
                    <div className="col-span-2 md:hidden">
                      <ClickableDescription>
                        The maximum hours of continuous work allowed before a meal break must be provided.
                      </ClickableDescription>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="grace-period" className={fieldLabelClass}>Grace Period</Label>
                          <CustomizedFieldIndicator isCustomized={mealConfigCustomized.gracePeriodMinutes} context={context} />
                        </div>
                      </div>
                      <div className="hidden md:block">
                        <ClickableDescription>
                          A buffer time that prevents surcharges for minor delays.
                        </ClickableDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end md:items-end md:w-full">
                      <NumberInput id="grace-period" unit="min" size="xs" {...(gracePeriodMinutesProps || {})} />
                      <AnimatedFormError className="w-full text-left md:text-right mt-1">
                        {gracePeriodMinutesError}
                      </AnimatedFormError>
                    </div>
                    <div className="col-span-2 md:hidden">
                      <ClickableDescription>
                        A buffer time that prevents surcharges for minor delays.
                      </ClickableDescription>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center md:grid-cols-2 md:gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="recurring-meal-window" className={fieldLabelClass}>Recurring Deadline</Label>
                          <CustomizedFieldIndicator isCustomized={mealConfigCustomized.recurringMealDeadline} context={context} />
                        </div>
                      </div>
                      <div className="hidden md:block">
                        <ClickableDescription>
                          {safeConfig.recurringMealDeadline
                            ? 'Each qualifying break resets the deadline. Longer shifts may require multiple breaks, and each missed deadline triggers its own surcharge.'
                            : 'Only one meal break deadline is imposed per shift. After the first qualifying break or surcharge, no additional break is required.'}
                        </ClickableDescription>
                      </div>
                    </div>
                    <div className="flex justify-end md:justify-end md:w-full">
                      <Switch
                        data-anim-audit="meal-recurring-deadline-switch"
                        id="recurring-meal-window"
                        checked={safeConfig.recurringMealDeadline}
                        onCheckedChange={(checked) => onMealConfigChange({ recurringMealDeadline: checked })}
                      />
                    </div>
                    <div className="col-span-2 md:hidden">
                      <ClickableDescription>
                        {safeConfig.recurringMealDeadline
                          ? 'Each qualifying break resets the deadline. Longer shifts may require multiple breaks, and each missed deadline triggers its own surcharge.'
                          : 'Only one meal break deadline is imposed per shift. After the first qualifying break or surcharge, no additional break is required.'}
                      </ClickableDescription>
                    </div>
                  </div>

                  {rolesRow}
                </div>
              </SurchargeMethodPanel>
            )}

            {isCountMode && (
              <SurchargeMethodPanel key="count" activePanelRef={activePanelRef} transitionDuration={fadeDuration}>
                <CountModeSettingsSection
                  context={context}
                  mealConfigCustomized={mealConfigCustomized}
                  safeConfig={safeConfig}
                  onMealConfigChange={onMealConfigChange}
                  anyHalfDayRoles={anyHalfDayRoles}
                  roles={roles}
                  minimumBillableHours={minimumBillableHours}
                  halfDayHours={halfDayHours}
                  rolesRow={rolesRow}
                  halfDayBreaksProps={halfDayBreaksProps}
                  halfDayBreaksError={halfDayBreaksError}
                />
              </SurchargeMethodPanel>
            )}
          </AnimatePresence>
        </div>
      </div>


    </div>
  );
};
